import { ALBIS_ROLE_LABEL } from "./albisVoice";
import { trackEvent } from "../analytics";
import { getSharePromptContext } from "./wedgeFunnel";
import { buildGoogleCalendarUrl, buildOutlookCalendarUrl } from "./googleCalendarLink";
import { mergeAndSortAttentionItems } from "./schoolAttention";
import {
  buildKitaExtractPartnerShareMessage,
  buildKitaPartnerShareMessage,
  buildKitaPeerShareMessage,
  buildWhatsAppSendUrl,
} from "./whatsAppShare";
import { openWhatsAppUrl } from "./shareMorningBriefing";
import { fetchMyInviteLink, inviteAbsoluteUrl } from "../api/briefingInvites";
import { ERSTE_HAUSHALTE_SCENARIO } from "./ersteHaushalte";

export { ERSTE_HAUSHALTE_SCENARIO };

/**
 * @param {string} scenario
 * @param {{ utmSource?: string }} [opts]
 */
export function kitaLandingUrl(scenario, { utmSource = "wa" } = {}) {
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "https://albis.life";
  const params = new URLSearchParams({ utm_scenario: scenario });
  if (utmSource) params.set("utm_source", utmSource);
  return `${origin}/kita?${params.toString()}`;
}

/**
 * Ready-to-paste Erste Haushalte invite (message + link) for WhatsApp / chats.
 * @param {{ utmSource?: string }} [opts]
 */
export function buildErsteHaushalteShareMessage({ utmSource = "wa" } = {}) {
  const url = kitaLandingUrl(ERSTE_HAUSHALTE_SCENARIO, { utmSource });
  return (
    `Wir bauen Albis — euer ${ALBIS_ROLE_LABEL}. Weniger Mental Load. Mehr Familienleben.\n\n` +
    `Noch klein, Zürich. Magst du als eines der ersten Haushalte mitdenken? Eine Frage, dann siehst du, was schon geht (Fristen aus KiTa-Mail oder Flyer).\n\n${url}`
  );
}

/**
 * Upcoming attention items (task/event) for WhatsApp handoff — not Infos titles.
 * @param {{ events?: object[], corrections?: object } | null | undefined} item
 */
export function getShareableKiTaEvents(item) {
  return mergeAndSortAttentionItems(
    item?.events || [],
    item?.corrections || null
  );
}

/**
 * @param {{ summary?: string, events?: object[] }} args
 * @returns {boolean}
 */
export function canShareKiTaExtract({ summary, events } = {}) {
  const hasEvents = Array.isArray(events) && events.length > 0;
  const hasSummary = String(summary || "").trim().length > 0;
  return hasEvents || hasSummary;
}

/**
 * @param {{ summary?: string, events?: object[] }} args
 * @returns {'events'|'summary_only'|'events_and_summary'|null}
 */
export function kitaExtractShareKind({ summary, events } = {}) {
  const hasEvents = Array.isArray(events) && events.length > 0;
  const hasSummary = String(summary || "").trim().length > 0;
  if (hasEvents && hasSummary) return "events_and_summary";
  if (hasEvents) return "events";
  if (hasSummary) return "summary_only";
  return null;
}

async function householdInviteUrl() {
  const { path } = await fetchMyInviteLink();
  return inviteAbsoluteUrl(path);
}

export async function shareKiTaPartnerViaWhatsApp() {
  const url = await householdInviteUrl();
  trackEvent("share_whatsapp", {
    scenario: "briefing_invite",
    trigger: "kita_partner",
  });
  openWhatsAppUrl(buildWhatsAppSendUrl(buildKitaPartnerShareMessage({ url })));
}

export async function shareKiTaPeerViaWhatsApp({ eventCount, source } = {}) {
  const url = kitaLandingUrl("kita_peer");
  const context = getSharePromptContext();
  const count = eventCount ?? context.eventCount;
  const origin =
    source ||
    (context.trigger === "photo_capture" || context.trigger === "capture"
      ? "capture"
      : null);
  trackEvent("share_whatsapp", { scenario: "kita_peer" });
  openWhatsAppUrl(
    buildWhatsAppSendUrl(
      buildKitaPeerShareMessage({ url, eventCount: count, source: origin }),
    ),
  );
}

/**
 * Hand off extraction content (events and/or summary) to co-parent via WhatsApp.
 * @param {{ summary?: string, events?: object[], extractionId?: string|null }} args
 */
export async function shareKiTaExtractViaWhatsApp({
  summary,
  events,
  extractionId = null,
} = {}) {
  const list = Array.isArray(events) ? events.filter(Boolean) : [];
  const summaryText = String(summary || "").trim();
  if (!canShareKiTaExtract({ summary: summaryText, events: list })) {
    throw new Error("Nichts zum Teilen — weder Termine noch Kurzfassung.");
  }

  const calendarUrls = list
    .map((event) => buildGoogleCalendarUrl(event))
    .filter(Boolean);
  const outlookUrls = list
    .map((event) => buildOutlookCalendarUrl(event))
    .filter(Boolean);
  let url = kitaLandingUrl("kita_extract_partner");
  try {
    url = await householdInviteUrl();
  } catch {
    /* signed-out or invite API down — keep landing fallback */
  }
  const shareKind = kitaExtractShareKind({ summary: summaryText, events: list });

  trackEvent("share_whatsapp", {
    scenario: "kita_extract_partner",
    event_count: list.length,
    has_summary: summaryText.length > 0,
    share_kind: shareKind,
    ...(extractionId ? { extraction_id: extractionId } : {}),
  });

  openWhatsAppUrl(
    buildWhatsAppSendUrl(
      buildKitaExtractPartnerShareMessage({
        summary: summaryText,
        events: list,
        calendarUrls,
        outlookUrls,
        url,
      })
    )
  );
}
